import { apiSlice } from "../../../features/api/apiSlice";

export type ChapterDietLite = {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  is_active: number | boolean;
  closed_at?: string | null;
  created_at?: string;
  updated_at?: string;
};
export type StateDTO = {
  id: number;
  name: string;
};
export type UserFull = {
  id: number;
  name: string;
  email?: string;
  whois?: string;
  status?: string;
};

export type Chapter = {
  id: string;
  name: string;
  message?: string;
  data?: any;
  created_at?: string;
  updated_at?: string;
  leaderships?: ChapterLeadership[];
  state_id?: number;
  state_name?: string;
};
export type CreateChapterPayload = {
  name: string;
  state_id: number;
};

export type UpdateChapterPayload = {
  id: string;
  name?: string;
  state_id?: number;
};
export type ChapterDiet = {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  is_active: boolean | number;
  closed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  leaderships?: ChapterLeadership[];
};

export type UserLite = { id: number; name: string; email?: string };
export type ChapterLeadership = {
  id: string;
  chapter_id: string;
  chapter_diet_id: string;
  chairman_id: number | null;
  secretary_id: number | null;
  created_at?: string;
  updated_at?: string;

  diet?: ChapterDietLite;
  chairman?: UserFull;
  secretary?: UserFull;
};

export type MemberSearchResult = {
  id: number | string;
  member_id?: string;
  membership_no?: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
  phone?: string;
};

export type InstitutionContact = {
  id: string;
  chapter_id: string;
  chapter_name?: string;
  institution_name: string;
  programme_type: 'bsc' | 'hnd' | 'other';
  programme_other?: string;
  hod_name: string;
  hod_email?: string;
  hod_phone?: string;
  accreditation_status: 'accredited' | 'unaccredited' | 'unknown';
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  additional_info?: string;
  submitted_by?: string;
  created_at?: string;
};

export type InstitutionContactPayload = {
  institution_name: string;
  programme_type: 'bsc' | 'hnd' | 'other';
  programme_other?: string;
  hod_name: string;
  hod_email?: string;
  hod_phone?: string;
  accreditation_status: 'accredited' | 'unaccredited' | 'unknown';
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  additional_info?: string;
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

/**
 * What a chapter leader's queue returns.
 *
 * `chapter` is the chapter they hold office in and names the screen. Where they
 * also ACT for chapters that have no sitting chairman, those appear in
 * `acting_for` and their applications are mixed into `data` — so each row's own
 * `chapter` is what identifies whose application it is, not the heading.
 */
export type ChapterQueueResponse = {
  chapter: any;
  chapter_id: string;
  chapter_ids?: string[];
  acting_for?: { id: string; name: string }[];
  data: any[];
};

/**
 * One row of a chapter's register. There is deliberately no money on it and no
 * id to open — the server does not send either.
 */
export type ChapterRegisterMember = {
  id: string;
  membership_no?: string | null;
  member_member_no?: string | null;
  title?: string | null;
  surname?: string | null;
  firstname?: string | null;
  othernames?: string | null;
  /** As it was typed in over the years: "M", "m", "Male", "F", "Female"… */
  gender?: string | null;
  email?: string | null;
  /** The account's state, joined as a scalar — there is no user object here. */
  account_status?: "active" | "inactive" | string | null;
  account_verified_at?: string | null;
  /**
   * 1 when a dues invoice is still open, null when none is. A flag and nothing
   * more — the balance itself is never sent to this screen.
   */
  owes_dues?: number | null;
  deceased?: number | boolean | null;
  reg_date?: string | null;
  grade?: { id: number | string; name: string } | null;
  chapter?: { id: string; name: string } | null;
  state?: { id: number; name: string } | null;
  lga?: { id: number; name: string } | null;
  sector?: { id: number; name: string } | null;
};

export type ChapterRegisterQuery = {
  page?: number;
  per_page?: number;
  search?: string;
  grade?: string;
  /** "male" / "female" — the server matches on the initial, not the spelling. */
  gender?: string;
  /** active | inactive | deceased */
  status?: string;
  /** verified | unverified */
  verified?: string;
  /** up_to_date | owing */
  dues?: string;
  chapter_id?: string;
  sort_by?: string;
  sort_dir?: string;
};

export type ChapterRegisterResponse = {
  chapter: any;
  chapter_id: string;
  chapter_ids?: string[];
  acting_for?: { id: string; name: string }[];
  /** Laravel's paginator, straight through — DataTable reads it as it stands. */
  members: {
    data: ChapterRegisterMember[];
    total: number;
    current_page: number;
    from: number | null;
    to: number | null;
    links: { url: string | null; label: string; active: boolean }[];
  };
};

export const chapterApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStates: builder.query<StateDTO[], void>({
      query: () => `/states`,
      providesTags: ["States"],
    }),

    /* ---------- CHAPTER DIETS ---------- */
    getChapterDiets: builder.query<ChapterDiet[], void>({
      query: () => "/chapter-diets",
      transformResponse: (res: any) => pick(res) || res || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((d: any) => ({
                type: "ChapterDiets" as const,
                id: d.id,
              })),
              { type: "ChapterDiets" as const, id: "LIST" },
            ]
          : [{ type: "ChapterDiets" as const, id: "LIST" }],
    }),

    createChapterDiet: builder.mutation<
      ChapterDiet,
      { name: string; start_date?: string; end_date?: string }
    >({
      query: (body) => ({
        url: "/chapter-diets",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ChapterDiets" as const, id: "LIST" }],
    }),

    getChapterDietDetails: builder.query<ChapterDiet, string>({
      query: (id) => `/chapter-diets/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "ChapterDiets" as const, id }],
    }),

    updateChapterDiet: builder.mutation<
      any,
      {
        id: string;
        name?: string;
        start_date?: string | null;
        end_date?: string | null;
        is_active?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/chapter-diets/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChapterDiets" as const, id },
        { type: "ChapterDiets" as const, id: "LIST" },
      ],
    }),

    closeChapterDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/chapter-diets/${id}/close`,
        method: "PUT",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ChapterDiets" as const, id },
        { type: "ChapterDiets" as const, id: "LIST" },
      ],
    }),

    /* ---------- CHAPTERS ---------- */
    getChapters: builder.query<Chapter[], void>({
      query: () => `/chapters`,
      providesTags: ["Chapters"],
    }),

    createChapter: builder.mutation<Chapter, CreateChapterPayload>({
      query: (body) => ({
        url: "/chapters",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Chapters" as const, id: "LIST" }],
    }),

    getChapterDetails: builder.query<Chapter, string>({
      query: (id) => `/chapters/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "Chapters" as const, id }],
    }),

    updateChapter: builder.mutation<any, UpdateChapterPayload>({
      query: ({ id, ...body }) => ({
        url: `/chapters/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Chapters" as const, id },
        { type: "Chapters" as const, id: "LIST" },
      ],
    }),

    deleteChapter: builder.mutation<any, string>({
      query: (id) => ({
        url: `/chapters/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Chapters" as const, id: "LIST" }],
    }),

    /* ---------- LEADERSHIP ---------- */
    assignLeadership: builder.mutation<
      any,
      {
        chapter_id: string;
        chapter_diet_id: string;
        chairman_id: number | null;
        secretary_id: number | null;
      }
    >({
      query: (body) => ({
        url: "/chapter-leaderships",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, body) => [
        { type: "ChapterDiets" as const, id: body.chapter_diet_id },
        { type: "Chapters" as const, id: body.chapter_id },
      ],
    }),

    removeLeadership: builder.mutation<any, string>({
      query: (id) => ({
        url: `/chapter-leaderships/${id}`,
        method: "DELETE",
      }),
    }),

    /* ---------- CHAPTER APPLICATIONS ---------- */
    getMyChapterApplications: builder.query<ChapterQueueResponse, string | void>({
      query: (status) => `/chapter/my-applications${status ? `?status=${status}` : ""}`,
      providesTags: [{ type: "Chapters" as const, id: "MY_APPS" }],
    }),

    chapterAcceptApplication: builder.mutation<any, string>({
      query: (id) => ({ url: `/chapter/applications/${id}/accept`, method: "POST" }),
      invalidatesTags: [{ type: "Chapters" as const, id: "MY_APPS" }],
    }),

    chapterRejectApplication: builder.mutation<any, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/chapter/applications/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: [{ type: "Chapters" as const, id: "MY_APPS" }],
    }),

    /* ---------- CHAPTER UPGRADE APPLICATIONS ---------- */
    getMyChapterUpgradeApplications: builder.query<ChapterQueueResponse, string | void>({
      query: (status) => `/chapter/my-upgrade-applications${status ? `?status=${status}` : ""}`,
      providesTags: [{ type: "Chapters" as const, id: "MY_UPGRADE_APPS" }],
    }),

    getChapterUpgradeApplicationDetails: builder.query<any, string>({
      query: (id) => `/chapter/upgrade-applications/${id}`,
      providesTags: [{ type: "Chapters" as const, id: "MY_UPGRADE_APPS" }],
    }),

    chapterAcceptUpgradeApplication: builder.mutation<any, { id: string; comment: string }>({
      query: ({ id, comment }) => ({
        url: `/chapter/upgrade-applications/${id}/accept`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: [{ type: "Chapters" as const, id: "MY_UPGRADE_APPS" }],
    }),

    chapterRejectUpgradeApplication: builder.mutation<any, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/chapter/upgrade-applications/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: [{ type: "Chapters" as const, id: "MY_UPGRADE_APPS" }],
    }),

    /* ---------- MEMBER SEARCH ---------- */
    searchChapterMembers: builder.query<MemberSearchResult[], string>({
      query: (q) => `/members/search/${encodeURIComponent(q)}`,
      transformResponse: (res: any) => {
        const result = pick(res);
        return Array.isArray(result) ? result : [];
      },
    }),

    /* ---------- CHAPTER REGISTER ---------- */
    // The chairman's own chapter. Which chapter that is comes off his
    // leadership record on the server, so nothing here selects it.
    getChapterRegister: builder.query<ChapterRegisterResponse, ChapterRegisterQuery | void>({
      query: (params) => ({ url: '/chapter/members', params: params ?? {} }),
      providesTags: [{ type: 'Chapters' as const, id: 'REGISTER' }],
    }),

    /* ---------- INSTITUTION CONTACTS ---------- */
    getMyInstitutionContacts: builder.query<InstitutionContact[], void>({
      query: () => '/chapter/institution-contacts',
      transformResponse: (res: any) => {
        const d = res?.data ?? res;
        return Array.isArray(d) ? d : [];
      },
      providesTags: [{ type: 'Chapters' as const, id: 'INSTITUTION_CONTACTS' }],
    }),

    submitInstitutionContact: builder.mutation<{ message: string; data: InstitutionContact }, InstitutionContactPayload>({
      query: (body) => ({ url: '/chapter/institution-contacts', method: 'POST', body }),
      invalidatesTags: [{ type: 'Chapters' as const, id: 'INSTITUTION_CONTACTS' }],
    }),

    deleteInstitutionContact: builder.mutation<{ message: string }, string>({
      query: (id) => ({ url: `/chapter/institution-contacts/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Chapters' as const, id: 'INSTITUTION_CONTACTS' }],
    }),

    adminGetInstitutionContacts: builder.query<InstitutionContact[], { chapter_id?: string; programme_type?: string; accreditation_status?: string } | void>({
      query: (params) => ({ url: '/admin/institution-contacts', params: params ?? {} }),
      transformResponse: (res: any) => {
        const d = res?.data ?? res;
        return Array.isArray(d) ? d : [];
      },
      providesTags: [{ type: 'Chapters' as const, id: 'INSTITUTION_CONTACTS_ADMIN' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetChapterDietsQuery,
  useCreateChapterDietMutation,
  useGetChapterDietDetailsQuery,
  useUpdateChapterDietMutation,
  useCloseChapterDietMutation,

  useGetChaptersQuery,
  useCreateChapterMutation,
  useGetChapterDetailsQuery,
  useUpdateChapterMutation,
  useDeleteChapterMutation,

  useAssignLeadershipMutation,
  useRemoveLeadershipMutation,

  useSearchChapterMembersQuery,
  useGetStatesQuery,
  useGetMyChapterApplicationsQuery,
  useChapterAcceptApplicationMutation,
  useChapterRejectApplicationMutation,
  useGetMyChapterUpgradeApplicationsQuery,
  useGetChapterUpgradeApplicationDetailsQuery,
  useChapterAcceptUpgradeApplicationMutation,
  useChapterRejectUpgradeApplicationMutation,

  useGetChapterRegisterQuery,

  useGetMyInstitutionContactsQuery,
  useSubmitInstitutionContactMutation,
  useDeleteInstitutionContactMutation,
  useAdminGetInstitutionContactsQuery,
} = chapterApi;
